import React, { useEffect, useRef, useState } from "react";
import { List, X, Phone } from "@phosphor-icons/react";
import { CleaningNavDropdown, MobileNavMenu } from "@/components/CleaningNavDropdown";

const BONE = "#F5F4F0";
const FOREST = "#1B2A22";
const TERRA = "#C84B31";
const TERRA_DARK = "#A03A24";
export const PHONE_DISPLAY = "(410) 870-9347";
export const PHONE_HREF = "tel:+14108709347";

const TOP_LINKS = [
  { href: "/service-area", label: "Service Area", testid: "nav-service-area" },
  { href: "/property-managers", label: "For Property Managers", testid: "nav-property-managers" },
];

// Full site header (brand, Services dropdown, Service Area, page anchors, call). Shared by / and /service-area.
export const SiteHeader = ({ anchors = [], brandTestId = "customers-brand-lockup" }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") || "/" : "";

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const apply = () => document.documentElement.style.setProperty("--site-header-h", `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty("--site-header-h"); };
  }, []);

  const mobileAnchors = anchors;

  return (
    <header ref={headerRef} className="sticky top-0 z-40 border-b border-[#1C1A17]/12 bg-[#F5F4F0]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-5 py-4 md:px-10">
        <a href="/" data-testid={brandTestId} className="group flex items-center gap-2 sm:gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-sm text-[#F5F4F0]" style={{ backgroundColor: FOREST }}>
            <span className="font-display text-[15px] font-black leading-none">H</span>
          </div>
          <div className="leading-none">
            <div className="font-display whitespace-nowrap text-[14px] font-black tracking-tight sm:text-[18px]">HCOB Property Services</div>
            <div className="font-mono-label mt-1 text-[9px] tracking-[0.24em] text-[#1C1A17]/60">Baltimore · Maryland</div>
          </div>
        </a>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          <CleaningNavDropdown linkClass="text-[13px] font-semibold text-[#1C1A17]/70 hover:text-[#1C1A17]" />
          {TOP_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              aria-current={path === l.href ? "page" : undefined}
              className={`text-[13px] font-semibold hover:text-[#1C1A17] ${path === l.href ? "text-[#C84B31]" : "text-[#1C1A17]/70"}`}
            >
              {l.label}
            </a>
          ))}
          {anchors.map((a) => (
            <a
              key={a.href}
              href={a.href}
              data-testid={`nav-${a.href.replace(/[#/?]/g, "")}`}
              className={
                a.primary
                  ? "rounded-sm border border-[#1C1A17] px-3.5 py-2 text-[13px] font-bold text-[#1C1A17] transition-colors hover:bg-[#1C1A17] hover:text-[#F5F4F0]"
                  : "text-[13px] font-semibold text-[#1C1A17]/70 hover:text-[#1C1A17]"
              }
            >
              {a.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="mobile-nav-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-sm border border-[#1C1A17]/20 text-[#1C1A17] md:hidden"
          >
            {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
          <a
            data-testid="header-phone-cta"
            href={PHONE_HREF}
            className="hidden sm:inline-flex items-center gap-2 rounded-sm px-4 py-2.5 text-[13px] font-bold text-[#F5F4F0] transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: TERRA }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TERRA_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TERRA)}
          >
            <Phone size={14} weight="fill" /> {PHONE_DISPLAY}
          </a>
          <a
            data-testid="header-phone-cta-mobile"
            href={PHONE_HREF}
            aria-label={`Call ${PHONE_DISPLAY}`}
            className="grid h-10 w-10 place-items-center rounded-sm text-[#F5F4F0] sm:hidden"
            style={{ backgroundColor: TERRA }}
          >
            <Phone size={16} weight="fill" />
          </a>
        </div>
      </div>
      <MobileNavMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchors={mobileAnchors} />
    </header>
  );
};

export { BONE, FOREST, TERRA, TERRA_DARK };
